import {
  LinkText,
  Price,
  RadioGroup,
  RadioOption,
  Stack,
  Tag,
  Text,
} from "flicket-ui";
import { useContext, useEffect, useState } from "react";
import { locateTerms } from "~components/checkout/ticket-cover/locateTerms";
import { useCheckoutContext } from "~context/checkoutContext";
import { OrderQuery } from "~graphql/sdk";
import { useOrganization, useSDK } from "~hooks";
import { getGraphQLError, handlePromise, showToast } from "~lib";
import { OrderSummaryContext } from "../OrderSummary/OrderSummaryContext";
import { useField, useFormikContext } from "formik";
import { SinglePageCheckoutFormValues } from "~features/single-page-checkout/forms/useSinglePageCheckoutForm";

const options = (
  currency: string,
  feeAmount: number,
  country?: string
): RadioOption[] => [
  {
    value: "yes",
    name: "Refundable tickets",
    nameSuffix: () => (
      <>
        <Text variant="regular">
          +
          <Price price={feeAmount} currency={currency} mr={1} />
          <Tag variant="solid" colorPalette="success" size="sm">
            Recommended
          </Tag>
        </Text>
      </>
    ),
    description: (
      <>
        Get a refund if you are unable to attend this event due to injury,
        illness or any other personal circumstances set out in the{" "}
        <LinkText href={locateTerms(country ?? "NZ")} target="_blank">
          terms and conditions
        </LinkText>{" "}
        which you acknowledge you have read and accepted.
      </>
    ),
  },
  {
    value: "no",
    name: "Non-refundable",
    description:
      "I understand my tickets are non refundable if I’m unable to attend.",
  },
];

export const SinglePageCheckoutTicketCover = () => {
  const { organization } = useOrganization();
  const { isSubmitting } = useFormikContext<SinglePageCheckoutFormValues>();
  const { isLoading, addToQueue } = useContext(OrderSummaryContext);
  const { order } = useCheckoutContext();
  const [feeAmount, setFeeAmount] = useState<number | null>(null);
  const { currency, address } = organization;
  const [field, meta, helpers] = useField("ticketCover");
  const [hasBeenTouched, setHasBeenTouched] = useState(false);

  const sdk = useSDK();

  const calculateTicketCoverPrice = async (order: OrderQuery["order"]) => {
    const { error, data } = await handlePromise(async () =>
      sdk.calculateTicketCoverAmounts({
        id: order.id,
      })
    );

    if (error) {
      const e = getGraphQLError(error);
      showToast(e.message, "error");
      return null;
    }

    return data.calculateTicketCoverAmounts.totalFeeAmount;
  };

  const onValueChange = (val: string) => {
    setHasBeenTouched(true);

    helpers.setValue(val === "yes");
    helpers.setError(undefined);

    addToQueue(`ticket-cover`, async () => {
      try {
        await sdk.updateTicketCoverForOrder({
          id: order?.id,
          input: { includeTicketCover: val === "yes" },
        });
      } catch (error) {
        console.error("Failed to update ticket cover:", error);
        showToast("Failed to update ticket cover", "error");
      }
    });
  };

  // This is in case there was an error applying ticket cover and we need to reset the value
  useEffect(() => {
    // Check hasBeenTouched so that we maintain an "undefined" value on first page load
    if (!isLoading && hasBeenTouched) {
      helpers.setValue(!!order?.orderTicketCover);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!order) {
      setFeeAmount(null);
      return;
    }

    void (async () => {
      const amount = await calculateTicketCoverPrice(order);
      setFeeAmount(amount);
    })();
  }, [order]);

  if (feeAmount === null) {
    return null;
  }

  return (
    <Stack direction={"vertical"} gap={1} id="ticketCover">
      <Text variant="header.S">Make your tickets refundable</Text>
      <RadioGroup
        variant="card"
        options={options(currency, feeAmount, address?.country)}
        value={(() => {
          if (field.value === undefined) return undefined;
          return field.value ? "yes" : "no";
        })()}
        onValueChange={onValueChange}
        disabled={isSubmitting}
        error={meta.error}
      />
    </Stack>
  );
};
